import type { ShopProduct, ShopProductVariant } from "@mall-mart/shared";
import type { Cart, CartItem } from "../commerce/Cart.js";
import type { CartManager } from "../commerce/CartManager.js";

/**
 * Cart review panel. With multiple stores in the mall you get one cart per
 * store; the panel lists every non-empty cart as its own section with its own
 * subtotal and Checkout button (each merchant checkout is a separate Shopify
 * session). Opens via the cart indicator in the HUD or the C key.
 */
export class CartPanel {
  private el: HTMLElement;
  private carts: CartManager;
  private productsFor: (shopId: string) => Promise<ShopProduct[]>;

  constructor(
    elementId: string,
    carts: CartManager,
    productsFor: (shopId: string) => Promise<ShopProduct[]>
  ) {
    const found: HTMLElement | null = document.getElementById(elementId);
    if (found === null) {
      throw new Error(`CartPanel element #${elementId} not found`);
    }
    this.el = found;
    this.carts = carts;
    this.productsFor = productsFor;

    carts.onChange(() => {
      if (!this.el.hidden) {
        this.render();
      }
    });

    document.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key === "Escape" && !this.el.hidden) {
        this.close();
      }
    });
  }

  open(): void {
    this.render();
    this.el.hidden = false;
  }

  close(): void {
    this.el.hidden = true;
  }

  toggle(): void {
    if (this.el.hidden) {
      this.open();
    } else {
      this.close();
    }
  }

  isOpen(): boolean {
    return !this.el.hidden;
  }

  private render(): void {
    this.el.replaceChildren();

    const closeBtn: HTMLButtonElement = document.createElement("button");
    closeBtn.className = "cp-close";
    closeBtn.textContent = "✕";
    closeBtn.addEventListener("click", () => this.close());
    this.el.appendChild(closeBtn);

    const h2: HTMLHeadingElement = document.createElement("h2");
    h2.textContent = "Your Cart";
    this.el.appendChild(h2);

    const nonEmpty: Cart[] = this.carts.nonEmptyCarts();
    if (nonEmpty.length === 0) {
      const empty: HTMLDivElement = document.createElement("div");
      empty.className = "cp-empty";
      empty.textContent = "Your cart is empty.";
      this.el.appendChild(empty);
      return;
    }

    for (const cart of nonEmpty) {
      this.renderCartSection(cart);
    }
  }

  private renderCartSection(cart: Cart): void {
    const store: HTMLDivElement = document.createElement("div");
    store.className = "cp-store";
    store.textContent = cart.shopName;
    this.el.appendChild(store);

    for (const item of cart.itemList()) {
      this.el.appendChild(this.renderItem(cart, item));
    }

    const total = cart.totalPrice();
    const footer: HTMLDivElement = document.createElement("div");
    footer.className = "cp-footer";

    const totalDiv: HTMLDivElement = document.createElement("div");
    totalDiv.className = "cp-total";
    totalDiv.textContent = `Total: ${total.amount} ${total.currency}`;
    footer.appendChild(totalDiv);

    const actions: HTMLDivElement = document.createElement("div");
    actions.className = "cp-actions";

    const clearBtn: HTMLButtonElement = document.createElement("button");
    clearBtn.className = "cp-clear";
    clearBtn.textContent = "Clear";
    clearBtn.addEventListener("click", () => {
      cart.clear();
      this.render();
    });
    actions.appendChild(clearBtn);

    const checkoutBtn: HTMLButtonElement = document.createElement("button");
    checkoutBtn.className = "cp-checkout";
    checkoutBtn.textContent = "Checkout →";
    checkoutBtn.addEventListener("click", () => {
      checkoutBtn.disabled = true;
      checkoutBtn.textContent = "Preparing…";
      cart
        .resolveCheckoutUrl()
        .then((url: string) => {
          window.open(url, "_blank", "noopener,noreferrer");
        })
        .catch((err: unknown) => {
          console.error(`[CartPanel] Checkout failed for ${cart.shopId}:`, err);
          checkoutBtn.textContent = "Checkout failed, retry?";
        })
        .finally(() => {
          checkoutBtn.disabled = false;
          if (checkoutBtn.textContent === "Preparing…") {
            checkoutBtn.textContent = "Checkout →";
          }
        });
    });
    actions.appendChild(checkoutBtn);

    footer.appendChild(actions);
    this.el.appendChild(footer);
  }

  private renderItem(cart: Cart, item: CartItem): HTMLDivElement {
    const row: HTMLDivElement = document.createElement("div");
    row.className = "cp-item";

    const img: HTMLImageElement = document.createElement("img");
    img.src = item.imageUrl;
    img.alt = item.productTitle;
    img.loading = "lazy";
    row.appendChild(img);

    const info: HTMLDivElement = document.createElement("div");
    const title: HTMLDivElement = document.createElement("div");
    title.className = "cp-title";
    title.textContent = item.productTitle;
    info.appendChild(title);
    const variant: HTMLDivElement = document.createElement("div");
    variant.className = "cp-variant";
    variant.textContent = item.variantTitle;
    info.appendChild(variant);
    this.upgradeToVariantSelect(cart, item, variant);
    const qty: HTMLDivElement = document.createElement("div");
    qty.className = "cp-qty";
    const subtotal: number = parseFloat(item.price) * item.quantity;
    qty.textContent = `${item.quantity} × ${item.price} ${item.currency}  =  ${subtotal.toFixed(2)} ${item.currency}`;
    info.appendChild(qty);
    row.appendChild(info);

    const removeBtn: HTMLButtonElement = document.createElement("button");
    removeBtn.className = "cp-remove";
    removeBtn.textContent = "Remove";
    removeBtn.addEventListener("click", () => {
      cart.remove(item.variantId);
      this.render();
    });
    row.appendChild(removeBtn);

    return row;
  }

  /** Last-minute variant switching: once the store's products load (cached
   *  after the first call), swap the static variant label for a select of
   *  the product's variants. Falls back to the plain label for
   *  single-variant products or when the lookup fails. */
  private upgradeToVariantSelect(cart: Cart, item: CartItem, label: HTMLDivElement): void {
    this.productsFor(cart.shopId)
      .then((products: ShopProduct[]) => {
        if (!label.isConnected) {
          return; // panel re-rendered or closed while products loaded
        }
        const product: ShopProduct | undefined = products.find((p: ShopProduct) =>
          p.variants.some((v: ShopProductVariant) => v.id === item.variantId)
        );
        if (product === undefined || product.variants.length < 2) {
          return;
        }
        const select: HTMLSelectElement = document.createElement("select");
        select.className = "cp-variant-select";
        for (const v of product.variants) {
          const option: HTMLOptionElement = document.createElement("option");
          option.value = v.id;
          option.textContent = v.title;
          if (v.availability !== "in_stock" && v.id !== item.variantId) {
            option.disabled = true;
            option.textContent = `${v.title} (unavailable)`;
          }
          select.appendChild(option);
        }
        select.value = item.variantId;
        select.addEventListener("change", () => {
          const chosen: ShopProductVariant | undefined = product.variants.find(
            (v: ShopProductVariant) => v.id === select.value
          );
          if (chosen !== undefined) {
            // changeVariant emits through CartManager, which re-renders the
            // open panel; no explicit render needed here.
            cart.changeVariant(item.variantId, product, chosen);
          }
        });
        label.replaceChildren(select);
      })
      .catch((err: unknown) => {
        console.warn(`[CartPanel] Variant options unavailable for ${cart.shopId}:`, err);
      });
  }
}
